import React, { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import { Toast } from 'react-vant'
import { toFixedNoRound, formatThousands } from '@/utils'
import { RecipientType } from '../index.type'
import { useUserInfo } from '@/hooks'

interface TransferData {
  currency: string
  currencyIcon: string
  amount: string
  recipientType: RecipientType
  recipientValue: string
  fee?: string
}

export default React.memo(function TransferConfirm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { userInfo } = useUserInfo()

  // 获取转账数据
  const transferData = useMemo(() => {
    const state = location.state as TransferData
    return state
  }, [location.state])

  // 如果没有数据，返回转账页面
  if (!transferData) {
    navigate('/transfer', { replace: true })
    return null
  }

  // 服务费（模拟数据，实际应从配置获取）
  const serviceFee = useMemo(() => {
    return transferData.fee || '0'
  }, [transferData.fee])

  // 取消转账
  const handleCancel = useCallback(() => {
    navigate(-1)
  }, [navigate])

  // 确认转账
  const handleConfirm = useCallback(() => {
    // 检查是否绑定邮箱或谷歌验证器
    if (!userInfo?.email && !userInfo?.is_bind_google) {
      Toast.fail(t('请先绑定邮箱或谷歌验证器'))
      return
    }

    // 构造转账参数
    const transferParams = {
      amount: transferData.amount,
      pay_type: transferData.currency.toLowerCase(), // usdt, usdc, usd1
      remark: '', // 备注，可选
      target_user_info: transferData.recipientValue,
      target_user_type: transferData.recipientType // uid, email, phone
    }

    // 构造成功页面数据
    const successData = {
      currency: transferData.currency,
      currencyIcon: transferData.currencyIcon,
      amount: transferData.amount,
      recipientType: transferData.recipientType,
      recipientValue: transferData.recipientValue,
      fee: serviceFee
    }

    // 跳转到选择验证方式页面
    navigate('/transfer/select-verification', {
      state: {
        transferData: transferParams,
        successData,
      }
    })
  }, [transferData, serviceFee, userInfo, navigate, t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-3.75 font-medium text-black">{t('转账')}</span>}
      />

      <div className="bg-white px-6 py-6 flex flex-col pb-24">
        {/* 转账图标 */}
        <div className="flex justify-center mb-6">
          <img src="/icon/exchange-icon.svg" alt="exchange" className="w-20 h-20" />
        </div>

        {/* 标题 */}
        <div className="text-center text-5 font-medium text-black mb-8">{t('发送')}</div>

        {/* 支付信息 */}
        <div className="mb-6">
          <div className="text-3.75 text-[#0F0F0F]/60 mb-3">{t('支付')}</div>
          <div className="flex items-center">
            <img src={transferData.currencyIcon} alt={transferData.currency} className="w-6 h-6 mr-3" />
            <span className="text-8 font-bold text-black">{formatThousands(transferData.amount, 2, true)}</span>
            <span className="text-8 text-[#979797] ml-2">{transferData.currency}</span>
          </div>
        </div>

        {/* 收款人信息 */}
        <div className="mb-8">
          <div className="text-3.75 text-[#0F0F0F]/60 mb-3">{t('到')}</div>
          <div className="text-3.5 text-black break-all">{transferData.recipientValue}</div>
        </div>

        {/* 详细信息 */}
        <div className="space-y-4 mb-8 border-[0.5px] border-t-solid border-[#E9E9E9] pt-5">
          {/* 服务费 */}
          <div className="flex flex-col">
            <span className="text-3.5 text-[#0F0F0F]/60">{t('服务费')}</span>
            <span className="text-3.5 text-black mt-2">{serviceFee}%</span>
          </div>
        </div>

        {/* 按钮组 */}
        <div className="fixed bottom-6 left-6 right-6 flex gap-4">
          <CustomButton
            color='default'
            onClick={handleCancel}
          >
            {t('取消')}
          </CustomButton>
          <CustomButton
            onClick={handleConfirm}
            className="flex-1"
          >
            {t('确认')}
          </CustomButton>
        </div>
      </div>
    </PageLayout>
  )
})
